/**
 * Teammates documentation page content.
 *
 * CRUD operations, tool assignment, instructions, metadata,
 * allowed senders, and trigger run overview.
 */

export const teammatesContent = `
A **teammate** is a reusable persona with a name, instructions, and tools. Configure once, run many times.

## Create a Teammate

\`\`\`python
from m8tes import M8tes

client = M8tes()

bot = client.teammates.create(
    name="Support Bot",
    tools=["slack", "linear"],
    instructions="Handle customer tickets efficiently",
    role="Customer support specialist",
    goals="Resolve tickets quickly and accurately",
)
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/teammates \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Support Bot",
    "tools": ["slack", "linear"],
    "instructions": "Handle customer tickets efficiently",
    "role": "Customer support specialist",
    "goals": "Resolve tickets quickly and accurately"
  }'
\`\`\`

### Fields

| Field | Type | Description |
|-------|------|-------------|
| \`name\` | string (required) | Display name for the teammate |
| \`instructions\` | string | System prompt — what this teammate does |
| \`tools\` | string[] | Tool names (e.g. \`["slack", "linear"]\`). See [Tools](/docs/skills) |
| \`role\` | string | Persona role, injected into prompt context |
| \`goals\` | string | Teammate goals, injected into system prompt |
| \`user_id\` | string | End-user ID for [multi-tenancy](/docs/users) |
| \`metadata\` | dict | Arbitrary key-value data stored with the teammate |
| \`allowed_senders\` | string[] | Email addresses or \`@domain\` patterns for [email inbox](/docs/email-inbox) |

## List Teammates

\`\`\`python
page = client.teammates.list()
for teammate in page.data:
    print(teammate.id, teammate.name)

# Auto-paginate through all
for teammate in client.teammates.list().auto_paging_iter():
    print(teammate.name)

# Filter by end-user
page = client.teammates.list(user_id="cust_123")
\`\`\`

\`\`\`bash
curl https://m8tes.ai/api/v2/teammates \\
  -H "Authorization: Bearer m8_your_key_here"

# Filter by end-user
curl "https://m8tes.ai/api/v2/teammates?user_id=cust_123" \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

## Get a Teammate

\`\`\`python
bot = client.teammates.get(teammate_id=1)
print(bot.name, bot.tools)
\`\`\`

\`\`\`bash
curl https://m8tes.ai/api/v2/teammates/1 \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

## Update a Teammate

Only provided fields are updated — omitted fields remain unchanged.

\`\`\`python
bot = client.teammates.update(
    teammate_id=1,
    instructions="Updated instructions",
    tools=["slack", "linear", "google-sheets"],
)
\`\`\`

\`\`\`bash
curl -X PATCH https://m8tes.ai/api/v2/teammates/1 \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"instructions": "Updated instructions", "tools": ["slack", "linear", "google-sheets"]}'
\`\`\`

## Delete a Teammate

Soft-deletes (archives) the teammate. Archived teammates won't appear in list results.

\`\`\`python
client.teammates.delete(teammate_id=1)
\`\`\`

\`\`\`bash
curl -X DELETE https://m8tes.ai/api/v2/teammates/1 \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

## Trigger Runs

There are three ways to trigger a run on a teammate:

| Method | Use Case |
|--------|----------|
| **[Message](/docs/runs)** | Call \`runs.create()\` with a message — the most common way |
| **[Webhook](/docs/webhook-triggers)** | External systems POST to a webhook URL to start a run |
| **[Email Inbox](/docs/email-inbox)** | Inbound emails automatically trigger runs |

## What's Next

- [Runs](/docs/runs) — execute teammates and get results
- [Tasks](/docs/tasks) — create reusable task definitions
- [Tools](/docs/skills) — browse and assign available tools
`;
